import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import { PlusIcon, XMarkIcon, EyeIcon, HeartIcon, PencilIcon, TrashIcon, ShareIcon, RectangleGroupIcon, CloudArrowUpIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { FaInstagram } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import AddItemsToFolderModal from './AddItemsToFolderModal';
import ShareModal from '../common/ShareModal';
import { Portfolio } from '../../types';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { deletePortfolioItem, removeBatchPortfolioFromState } from '../../store/slices/userSlice';
import AddPortfolioModal from './AddPortfolioModal';
import PortfolioViewModal from './PortfolioViewModal';
import EditPortfolioModal from './EditPortfolioModal';
import PortfolioViewModalV2 from './PortfolioViewModalV2';
import { getOptimizedImageUrl, resolveImageUrl } from '../../utils/image';
import { useInView } from 'react-intersection-observer';
import { portfolioAPI, genericAPIs } from '../../services/api';
import SmartImage from '../common/SmartImage';

const InstagramIcon = FaInstagram as any;

interface MasonryPortfolioGridProps {
  portfolio: Portfolio[];
  isOwnProfile: boolean;
  canAdd?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onPortfolioAdded?: (newItem: Portfolio) => void;
  onAddClick?: () => void;
  onSyncInstagram?: () => void;
  initialPortfolioId?: string;
  folderId?: string;
}

const MasonryPortfolioGrid: React.FC<MasonryPortfolioGridProps> = ({ 
  portfolio, 
  isOwnProfile,
  canAdd = true,
  loading = false,
  hasMore = false,
  onLoadMore,
  onPortfolioAdded,
  onAddClick,
  onSyncInstagram,
  initialPortfolioId,
  folderId
}) => {
  const [selectedImage, setSelectedImage] = useState<Portfolio | null>(null);
  const [imageRatios, setImageRatios] = useState<Record<string, number>>({});
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [shareItem, setShareItem] = useState<Portfolio | null>(null);
  const [showAddFromExistingModal, setShowAddFromExistingModal] = useState(false);
  const [showUploadNewModal, setShowUploadNewModal] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const { profileUser } = useAppSelector((state) => state.user);
  const { user } = useAppSelector((state) => state.auth);
  const [key, setKey] = useState(0); // Force re-render on resize

  // Handle initial portfolio ID
  useEffect(() => {
    if (initialPortfolioId) {
      const existingItem = portfolio.find(p => p.id === initialPortfolioId);
      if (existingItem) {
        setSelectedImage(existingItem);
      } else {
        // Fetch it
        const fetchItem = async () => {
          try {
            const response = await portfolioAPI.getPortfolioItem(initialPortfolioId);
            if (response.data.success && response.data.data) {
                setSelectedImage(response.data.data);
            }
          } catch (error) {
            console.error("Failed to fetch initial portfolio item", error);
          }
        };
        fetchItem();
      }
    }
  }, [initialPortfolioId]); // Only run when ID changes

  // Construct portfolios list for modal (include fetched item if not in list)
  const modalPortfolios = useMemo(() => {
      if (selectedImage && !portfolio.find(p => p.id === selectedImage.id)) {
          return [selectedImage, ...portfolio];
      }
      return portfolio;
  }, [selectedImage, portfolio]);

  useEffect(() => {
    let lastWidth = window.innerWidth;
    const handleResize = () => {
      if (window.innerWidth !== lastWidth) {
        lastWidth = window.innerWidth;
        setKey(prev => prev + 1);
      }
    };
    window.addEventListener('resize', handleResize);
    // Trigger once on mount to ensure correct layout - wait for page transition (300ms)
    setTimeout(() => setKey(prev => prev + 1), 400);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dispatch = useAppDispatch();

  // Infinite scroll observer
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '800px',
  });

  useEffect(() => {
    if (inView && hasMore && !loading && onLoadMore) {
      console.log('🔄 Infinite scroll triggered');
      onLoadMore();
    }
  }, [inView, hasMore, loading, onLoadMore]);

  // Masonry breakpoint configuration
  const breakpointColumnsObj = {
    default: 5,    // 5 columns on very large screens
    1536: 4,       // 4 columns on 2xl screens
    1280: 3,       // 3 columns on xl screens
    1024: 3,       // 3 columns on lg screens
    768: 2,        // 2 columns on md screens
    500: 3         // 3 columns on mobile (Instagram-style)
  };

  // Load image to get natural aspect ratio for better masonry layout
  useEffect(() => {
    portfolio.forEach(item => {
      if (!imageRatios[item.id]) {
        const img = new Image();
        img.onload = () => {
          const ratio = img.naturalWidth / img.naturalHeight;
          setImageRatios(prev => ({
            ...prev,
            [item.id]: ratio
          }));
        };
        img.src = item.thumbnail_image_url_signed || resolveImageUrl(item.thumbnail_image_url) || getOptimizedImageUrl(item.image_url, 'thumbnail');
      }
    });
  }, [portfolio, imageRatios]);

  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const toggleSelectItem = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectable = portfolio.filter(p => !p.is_collaboration).map(p => p.id);
    if (selectedItemIds.size === selectable.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(selectable));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedItemIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItemIds.size} selected portfolio item(s)?`)) return;

    setIsBatchDeleting(true);
    try {
      const ids = Array.from(selectedItemIds);
      await genericAPIs.batchDeletePortfolioItems(ids);
      toast.success(`${ids.length} portfolio item(s) deleted successfully!`);
      ids.forEach(id => dispatch(deletePortfolioItem(id)));
      setSelectedItemIds(new Set());
      setIsSelectMode(false);
    } catch (err) {
      toast.error('Failed to delete selected portfolio items');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleAddPortfolio = () => {
    if (onAddClick) {
      onAddClick();
    }
  };

  const handleDeletePortfolio = async (portfolioId: string) => {
    if (window.confirm('Are you sure you want to delete this portfolio item?')) {
      await dispatch(deletePortfolioItem(portfolioId));
    }
  };

  // Create items array with add button for own profile
  const masonryItems = [];
  
  // Add "Add New" item at the beginning for own profile
  if (isOwnProfile && canAdd) {
    masonryItems.push({
      id: 'add-new',
      type: 'add',
      component: (
        <div className="relative group">
          <motion.div
            key="add-new"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (folderId) {
                setIsAddMenuOpen(!isAddMenuOpen);
              } else {
                handleAddPortfolio();
              }
            }}
            className={`cursor-pointer bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed rounded-2xl md:rounded-3xl overflow-hidden transition-all duration-300 h-32 md:h-72 ${
              isAddMenuOpen 
                ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-800' 
                : 'border-gray-200 dark:border-gray-700 hover:border-black dark:hover:border-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <div className="h-full flex flex-col items-center justify-center p-3 md:p-6 text-center">
              <div className="bg-white dark:bg-gray-800 p-2 md:p-5 rounded-full shadow-sm group-hover:shadow-md transition-all mb-2 md:mb-4 group-hover:scale-110 transform">
                <PlusIcon className="h-5 w-5 md:h-8 md:w-8 text-gray-400 dark:text-gray-500 group-hover:text-black dark:group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 dark:text-white group-hover:text-black dark:group-hover:text-white transition-colors mb-1 md:mb-2 leading-tight">
                {folderId ? 'Add to Folder' : 'Add Work'}
              </h3>
              <p className="text-[10px] md:text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors hidden md:block">
                {folderId ? 'Select existing or upload new' : 'Share your latest creation'}
              </p>
            </div>
          </motion.div>

          {/* Special Menu for Folder View */}
          <AnimatePresence>
            {isAddMenuOpen && folderId && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setIsAddMenuOpen(false)} 
                />
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute top-full left-0 right-0 mt-3 p-2 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-20 overflow-hidden"
                >
                  <button
                    onClick={() => {
                      setShowUploadNewModal(true);
                      setIsAddMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg shrink-0">
                      <CloudArrowUpIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Upload New</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">Add a fresh work to this folder</p>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setShowAddFromExistingModal(true);
                      setIsAddMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg shrink-0">
                      <RectangleGroupIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Select Existing</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">Pick items from your portfolio</p>
                    </div>
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )
    });
  }

  // Add portfolio items
  portfolio.forEach((item, index) => {
    const aspectRatio = imageRatios[item.id] || 1; // Default square while loading
    
    masonryItems.push({
      id: item.id,
      type: 'portfolio',
      component: (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: (index + (isOwnProfile ? 1 : 0)) * 0.1 }}
          className="group cursor-pointer relative mb-6"
          onClick={() => {
            if (isSelectMode && !item.is_collaboration) {
              toggleSelectItem(item.id);
            } else {
              setSelectedImage(item);
            }
          }}
        >
          <div 
            className="relative overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800 shadow-sm hover:shadow-xl transition-all duration-500"
            style={{ aspectRatio: aspectRatio }}
          >
            {isSelectMode && !item.is_collaboration && (
              <div 
                onClick={(e) => toggleSelectItem(item.id, e)}
                className="absolute top-3 left-3 z-30 cursor-pointer p-1 rounded-full bg-black/40 backdrop-blur-md transition hover:scale-110"
              >
                {selectedItemIds.has(item.id) ? (
                  <CheckCircleIcon className="w-7 h-7 text-indigo-500 fill-white" />
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-white/80 bg-black/20" />
                )}
              </div>
            )}
            <SmartImage
              src={item.thumbnail_image_url_signed || resolveImageUrl(item.thumbnail_image_url) || getOptimizedImageUrl(item.image_url, 'thumbnail')}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              style={{
                transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
              }}
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block" />
            
            {/* Content Overlay */}
            <div className="absolute inset-0 hidden md:flex flex-col justify-between p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {/* Top Actions (Owner Only - Not for Collaborations) */}
              {isOwnProfile && !item.is_collaboration && (
                <div className="flex justify-end space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation(); // Prevent image click
                      setEditingPortfolio(item);
                    }}
                    className="p-2 bg-white/20 backdrop-blur-md rounded-full hover:bg-white text-white hover:text-black transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                    
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation(); // Prevent image click
                      console.log('🗑️ Delete clicked for:', item.id);
                      handleDeletePortfolio(item.id);
                    }}
                    className="p-2 bg-white/20 backdrop-blur-md rounded-full hover:bg-red-500 text-white transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </motion.button>
                  {/* Edit Portfolio Modal */}
                  {editingPortfolio && (
                      <EditPortfolioModal
                      portfolio={editingPortfolio}
                      onClose={() => setEditingPortfolio(null)}
                      onSuccess={() => setEditingPortfolio(null)}
                      />
                  )}               
                </div>
              )}

              {/* Share Button (Visible to everyone) */}
              <div className={`absolute top-5 right-5 ${isOwnProfile && !item.is_collaboration ? 'mr-24' : ''}`}>
                  <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShareItem(item);
                      }}
                      className="p-2 bg-black/20 backdrop-blur-md rounded-full hover:bg-white text-white hover:text-black transition-colors opacity-0 group-hover:opacity-100"
                      title="Share"
                  >
                      <ShareIcon className="h-4 w-4" />
                  </motion.button>
              </div>


              {/* Bottom Info */}
              <div className="space-y-3 mt-auto">
                {/* Title and Tags */}
                <div>
                  <h3 className="text-white font-bold text-base mb-1 line-clamp-2 leading-tight">
                    {item.title}
                  </h3>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {item.tags.slice(0, 3).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-2.5 py-1 text-[10px] font-semibold bg-white text-black rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                      {item.tags.length > 3 && (
                        <span className="px-2.5 py-1 text-[10px] font-semibold bg-white/20 text-white rounded-full backdrop-blur-sm">
                          +{item.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <div className="flex items-center space-x-4 text-white/90 text-xs font-medium">
                    <div className="flex items-center space-x-1.5">
                      <EyeIcon className="h-3.5 w-3.5" />
                      <span>{item.views || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <HeartIcon className="h-3.5 w-3.5" />
                      <span>{item.likes || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )
    });
  });

  return (
    <div>
      {/* Selection Control Bar (Owner Only) */}
      {isOwnProfile && portfolio.length > 0 && (
        <div className="flex justify-between items-center mb-4 px-1 min-w-0">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {isSelectMode ? `${selectedItemIds.size} item(s) selected` : ''}
          </div>
          <button
            type="button"
            onClick={() => {
              setIsSelectMode(!isSelectMode);
              setSelectedItemIds(new Set());
            }}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border ${
              isSelectMode
                ? 'bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-white border-gray-300 dark:border-gray-700'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Squares2X2Icon className="w-4 h-4" />
            {isSelectMode ? 'Cancel Selection' : 'Select Items'}
          </button>
        </div>
      )}

      {/* Masonry Layout */}
      <div className="w-full max-w-full min-w-0 overflow-hidden">
        <Masonry
          key={key}
          breakpointCols={breakpointColumnsObj}
          className="masonry-grid"
          columnClassName="masonry-grid_column"
        >
          {masonryItems.map(item => item.component)}
        </Masonry>
      </div>

      {/* Floating Batch Delete Action Bar */}
      {isSelectMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900/95 dark:bg-gray-900/95 text-white px-5 py-3 rounded-2xl shadow-2xl backdrop-blur-md border border-gray-800 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4">
          <span className="text-xs font-medium">
            {selectedItemIds.size} item(s) selected
          </span>
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-xs font-semibold text-gray-300 hover:text-white underline transition"
          >
            {selectedItemIds.size === portfolio.filter(p => !p.is_collaboration).length ? 'Deselect All' : 'Select All'}
          </button>
          <button
            type="button"
            disabled={selectedItemIds.size === 0 || isBatchDeleting}
            onClick={handleBatchDelete}
            className="px-4 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-lg"
          >
            <TrashIcon className="w-4 h-4" />
            {isBatchDeleting ? 'Deleting...' : `Delete Selected (${selectedItemIds.size})`}
          </button>
        </div>
      )}

      {/* Infinite Scroll Sentinel */}
      {hasMore && (
        <div ref={ref} className="h-20 flex justify-center items-center w-full mt-8">
          {loading && (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 dark:border-white"></div>
          )}
        </div>
      )}

      {/* Portfolio View Modal */}
      {selectedImage && (
        <>
        {/* {console.log('🎯 Rendering PortfolioViewModal with:', selectedImage)} */}
        <PortfolioViewModalV2
          portfolioId={selectedImage.id}
          onClose={() => {
              setSelectedImage(null);
          }}
          isOwnProfile = {isOwnProfile}
          source="profile"
          portfolios={modalPortfolios}
        />
        </>
        
      )}

      {/* Empty State */}
      {portfolio.length === 0 && !isOwnProfile && (
        <div className="text-center py-16">
          <div className="text-gray-400 dark:text-gray-600 mb-4">
            <EyeIcon className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No portfolio items</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">This user hasn't uploaded any work yet.</p>
        </div>
      )}

      {/* Share Modal */}
      {shareItem && (
        <ShareModal
            isOpen={!!shareItem}
            onClose={() => setShareItem(null)}
            title={shareItem.title}
            url={`${window.location.origin}/profile/${shareItem.user_id}/portfolio/${shareItem.id}`}
            type="portfolio"
            data={shareItem}
        />
      )}

      {showUploadNewModal && (
        <AddPortfolioModal 
          onClose={() => setShowUploadNewModal(false)}
          preSelectedFolderId={folderId}
          onSuccess={(newItem) => {
            setShowUploadNewModal(false);
            if (onPortfolioAdded) onPortfolioAdded(newItem);
          }}
        />
      )}

      {showAddFromExistingModal && folderId && (
        <AddItemsToFolderModal 
          onClose={() => setShowAddFromExistingModal(false)}
          onSuccess={() => {
            setShowAddFromExistingModal(false);
            if (onPortfolioAdded) {
              // Trigger a refresh in ProfilePage by passing a dummy item
              onPortfolioAdded({} as any); 
            }
          }}
          targetUserId={user?.id || ''}
          folderId={folderId}
          folderName="this folder"
        />
      )}
    </div>
  );
};

export default MasonryPortfolioGrid;
